import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Landmark, Hand, Camera, Cpu, Footprints, Lock,
  MonitorDot, Database, Clock, MoonStar, ArrowRight,
  ArrowUpRight, ChevronRight, Users, Radar, ShieldAlert, FileCheck2,
  Fingerprint, Trash2, Thermometer, Flame, Waves,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import {
  ASSETS, RECORRIDO, VISTAS, CONSTRAINTS, ARCH_FLOW, USE_CASES,
  FUNCTIONAL, SPECS, FASES,
} from "@/data/content";

const ICONS = {
  ShieldCheck, Landmark, Hand, Camera, Cpu, Footprints, Lock, MonitorDot,
  Database, Clock, MoonStar, FileCheck2, Fingerprint, Trash2, Thermometer,
  Flame, Waves, Radar,
};

const Icon = ({ name, ...props }) => {
  const C = ICONS[name] || ShieldCheck;
  return <C {...props} />;
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

function Overline({ children, testid }) {
  return (
    <span
      data-testid={testid}
      className="font-mono text-xs uppercase tracking-[0.22em] text-blue-400"
    >
      {children}
    </span>
  );
}

/*
 * Aquí vivían <EstadoBadge> y <LeyendaEstados>: la insignia que llevaba cada paso del recorrido,
 * cada vista y cada requisito, más la leyenda fija bajo el navbar. Se quitaron el 6-ago a
 * petición de Neto, que las lee como indicativos internos del proyecto. No fue un descuido y no
 * se reponen sin decisión del cliente. El porqué largo está en el encabezado de data/content.js.
 */

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="bg-slate-950 text-slate-100">
      <Navbar />

      {/* HERO */}
      {/* 4rem = la altura del navbar (h-16). Antes eran 7rem porque debajo del
          navbar iba la leyenda de estados, que se quito el 6-ago; si no se ajusta,
          el hero deja una franja muerta del alto de la leyenda que ya no existe. */}
      <section className="relative min-h-[calc(100vh-4rem)] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ASSETS.heroDrone}
            alt="Monitoreo de evento masivo"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/60" />
          <div className="absolute inset-0 tech-grid opacity-40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full py-20">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.12 } } }}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-300 text-xs font-mono">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-400 live-dot" />
                PLATAFORMA DE MONITOREO PARA EVENTOS MASIVOS
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              data-testid="hero-title"
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white"
            >
              El evento completo desde{" "}
              <span className="text-blue-500">una sola pantalla</span>.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
            >
              Kootixli registra en los accesos a quien entra, arma con eso una base que vive
              únicamente durante el evento, y la pone en un centro de mando: identificación de
              personas, aforo por zona, avisos de contingencia y vista térmica. Al cerrar el
              evento, la base se elimina y se entrega la constancia.
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="mt-4 text-sm text-slate-400 max-w-2xl leading-relaxed"
            >
              Hoy funciona la identificación por forma de caminar, probada sobre video real.
              Lo demás se muestra como demostración de cómo se verá.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-4">
              <a
                href="#recorrido"
                data-testid="hero-learn-btn"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-md transition-colors shadow-lg shadow-blue-600/20"
              >
                Ver cómo funciona <ArrowRight className="h-4 w-4" />
              </a>
              <button
                data-testid="hero-demo-btn"
                onClick={() => navigate("/demo")}
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-200 font-medium px-6 py-3 rounded-md transition-colors"
              >
                Abrir el centro de mando
              </button>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400"
            >
              <span className="flex items-center gap-2"><Footprints className="h-4 w-4 text-emerald-400" /> Identifica con el rostro cubierto</span>
              <span className="flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-blue-400" /> Consentimiento en los accesos</span>
              <span className="flex items-center gap-2"><Trash2 className="h-4 w-4 text-blue-400" /> Se borra al cerrar</span>
              <span className="flex items-center gap-2"><Landmark className="h-4 w-4 text-blue-400" /> LFPDPPP · LGPDPPSO</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* EL PROBLEMA DEL EVENTO */}
      <section id="solucion" className="relative py-24 lg:py-32 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <Overline testid="problem-overline">El problema</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              En un evento masivo, nadie sabe quién está dentro.
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed">
              Miles de personas entran en minutos. Las cámaras graban, pero el video crudo no
              responde preguntas: cuando pasa algo, no hay contra qué buscar, y el riesgo se ve
              cuando ya se formó.
            </p>
            <div className="mt-8 space-y-4">
              {[
                "Entran miles de personas y no queda registro de quién es quién.",
                "Si ocurre un incidente, hay horas de video y ninguna forma de buscar dentro.",
                "La zona saturada o la salida bloqueada se descubren cuando ya son un problema.",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                  <span className="text-slate-300 text-sm">{t}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="relative"
          >
            <div className="relative rounded-xl overflow-hidden border border-slate-800 bg-slate-900">
              <img src={ASSETS.gaitVisual} alt="Forma de caminar" className="w-full object-cover" />
              <div className="absolute top-4 left-4 px-3 py-1.5 rounded-md bg-slate-950/80 border border-blue-500/40 font-mono text-xs text-blue-300">
                Forma de caminar · única como una huella
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-500 font-mono leading-relaxed">
              Probado sobre video real: en clips controlados el motor distinguió a tres personas
              distintas sin confundirlas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* RECORRIDO DEL SISTEMA — seis pasos */}
      <section id="recorrido" className="relative py-24 lg:py-32 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <Overline testid="recorrido-overline">Cómo funciona</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              El recorrido completo, del acceso al borrado.
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              Seis pasos en orden. Cada uno dice si ya está construido, si es desarrollo
              pendiente, o si depende del equipo que ponga el cliente.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {RECORRIDO.map((p, i) => (
              <motion.div
                key={p.n}
                initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={fadeUp} transition={{ delay: i * 0.07 }}
                data-testid={`recorrido-step-${p.n}`}
                className="relative rounded-xl border border-slate-800 bg-slate-900/60 p-6 flex flex-col"
              >
                <span className="font-display font-black text-3xl text-slate-700">{`0${p.n}`}</span>
                <div className="h-11 w-11 rounded-lg bg-blue-600/15 border border-blue-600/30 flex items-center justify-center mt-4 mb-4">
                  <Icon name={p.icon} className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="font-display font-semibold text-lg text-white leading-snug">{p.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CENTRO DE MANDO — las cuatro vistas */}
      <section id="centro-de-mando" className="relative py-24 lg:py-32 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <Overline testid="mando-overline">Centro de mando</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              Una pantalla, cuatro vistas que se prenden.
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              El operador enciende la vista que necesita según lo que esté pasando. No son cuatro
              productos: es un solo tablero.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {VISTAS.map((v, i) => (
              <motion.div
                key={v.id}
                initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={fadeUp} transition={{ delay: i * 0.08 }}
                data-testid={`vista-card-${v.id}`}
                className="rounded-xl border border-slate-800 bg-slate-900/60 p-6"
              >
                <div className="h-11 w-11 rounded-lg bg-blue-600/15 border border-blue-600/30 flex items-center justify-center shrink-0">
                  <Icon name={v.icon} className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="mt-5 font-display font-semibold text-lg text-white leading-snug">{v.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10">
            <button
              data-testid="mando-open-btn"
              onClick={() => navigate("/demo")}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-md transition-colors shadow-lg shadow-blue-600/20"
            >
              Abrir el centro de mando <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* PRIVACIDAD Y MANEJO DE DATOS */}
      <section id="privacidad" className="relative py-24 lg:py-32 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <Overline testid="ethics-overline">Privacidad y manejo de datos</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              Quién responde por los datos, cuánto viven y cómo se borran.
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              La forma de caminar y el rostro son datos personales sensibles en México. Estas
              cuatro reglas son las que hacen que el sistema pueda operarse.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CONSTRAINTS.map((c, i) => (
              <motion.div
                key={c.title}
                initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={fadeUp} transition={{ delay: i * 0.08 }}
                data-testid={`constraint-card-${i}`}
                className="group relative rounded-xl border border-slate-800 bg-slate-900/60 p-6 hover:border-blue-600/60 transition-colors"
              >
                <div className="h-11 w-11 rounded-lg bg-blue-600/15 border border-blue-600/30 flex items-center justify-center mb-5">
                  <Icon name={c.icon} className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="font-display font-semibold text-lg text-white leading-snug">{c.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                <span className="mt-5 inline-block font-mono text-[11px] uppercase tracking-wider text-blue-500">
                  {c.tag}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LA CADENA, DE LA CÁMARA AL BORRADO */}
      <section id="arquitectura" className="relative py-24 lg:py-32 border-t border-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={ASSETS.fibers} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <Overline testid="arch-overline">Arquitectura</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              De la cámara del acceso al borrado del cierre.
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              La misma información recorre seis eslabones. El video se procesa lo más cerca
              posible de donde se captura, y lo que viaja son datos, no video crudo.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {ARCH_FLOW.map((step, i) => (
              <motion.div
                key={step.title}
                initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={fadeUp} transition={{ delay: i * 0.07 }}
                data-testid={`arch-step-${i}`}
                className="relative rounded-lg border border-slate-800 bg-slate-900/80 backdrop-blur p-5"
              >
                <span className="font-mono text-[11px] text-slate-600">{`0${i + 1}`}</span>
                <Icon name={step.icon} className="h-6 w-6 text-blue-400 mt-3" />
                <h4 className="mt-3 font-semibold text-sm text-white leading-snug">{step.title}</h4>
                <p className="mt-1 text-xs text-slate-500 leading-snug">{step.sub}</p>
                {i < ARCH_FLOW.length - 1 && (
                  <ChevronRight className="hidden lg:block absolute -right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-700" />
                )}
              </motion.div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {[
              { icon: Camera, label: "Cámaras, sensor térmico y drones: equipo del cliente" },
              { icon: Lock, label: "Cifrado en tránsito y en reposo" },
              { icon: FileCheck2, label: "Bitácora auditable de cada consulta" },
            ].map((b) => (
              <span key={b.label} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-800 bg-slate-900/60 text-xs text-slate-300">
                <b.icon className="h-4 w-4 text-blue-400" /> {b.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CASOS DE USO */}
      <section className="relative py-24 lg:py-32 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          <div>
            <Overline testid="usecases-overline">Casos de uso</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              Lo que el operador puede hacer.
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              Cuatro cosas concretas que resuelve el centro de mando durante el evento. El
              registro de quien entra es lo que hace posibles las otras tres.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {USE_CASES.map((u) => (
                <div
                  key={u.code}
                  data-testid={`usecase-${u.code}`}
                  className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 hover:border-blue-600/50 transition-colors"
                >
                  <span className="font-mono text-xs text-blue-500">{u.code}</span>
                  <h4 className="mt-2 font-semibold text-white text-sm leading-snug">{u.title}</h4>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">{u.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative rounded-xl overflow-hidden border border-slate-800">
            <img src={ASSETS.commandCenter} alt="Centro de mando" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <div className="flex items-center gap-2 text-blue-300 text-xs font-mono mb-2">
                <Users className="h-4 w-4" /> Centro de mando · tres roles
              </div>
              <p className="text-sm text-slate-200">
                Operador de monitoreo · Analista · Mando del evento
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MANEJO DE LA INFORMACIÓN + REQUISITOS */}
      <section id="especificaciones" className="relative py-24 lg:py-32 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <Overline testid="specs-overline">Manejo de la información</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              Cómo se cuida lo que se captura.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-16">
            {SPECS.map((s, i) => (
              <div
                key={s.label}
                data-testid={`spec-${i}`}
                className="rounded-lg border border-slate-800 bg-slate-900/60 p-6 flex items-center gap-4"
              >
                <div className="h-11 w-11 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                  <Icon name={s.icon} className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <div className="font-display font-bold text-xl text-white leading-tight">{s.value}</div>
                  <div className="mt-1 text-xs text-slate-500 leading-snug">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mb-6">
            <h3 className="font-display font-semibold text-xl text-white">
              Lo que hace el sistema, pieza por pieza
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              El alcance funcional completo, línea por línea.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-x-10 gap-y-3">
            {FUNCTIONAL.map((f) => (
              <div
                key={f.id}
                data-testid={`func-${f.id}`}
                className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4"
              >
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-xs text-blue-500">{f.id}</span>
                  <p className="mt-2 text-sm text-slate-300 leading-snug">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FASES */}
      <section id="fases" className="relative py-24 lg:py-32 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <Overline testid="roadmap-overline">Fases</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight text-white">
              Qué se entrega primero y qué es posterior.
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              El alcance está partido a propósito, para que quede claro qué se entrega primero.
            </p>
          </div>

          <div className="relative grid md:grid-cols-4 gap-4">
            {FASES.map((f, i) => (
              <motion.div
                key={f.fase}
                initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={fadeUp} transition={{ delay: i * 0.1 }}
                data-testid={`fase-${i}`}
                className={`relative rounded-xl border p-6 flex flex-col ${
                  f.fase === "Fase 1" ? "border-blue-600 bg-blue-600/10" : "border-slate-800 bg-slate-900/60"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-blue-400">{f.fase}</span>
                </div>
                <h4 className="mt-4 font-semibold text-white text-sm leading-snug">{f.title}</h4>
                <p className="mt-3 text-xs text-slate-400 leading-relaxed flex-1">{f.detalle}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="demo" className="relative py-24 lg:py-32 border-t border-slate-900 overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-30" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="flex justify-center gap-2 mb-6 flex-wrap">
            {["LFPDPPP", "LGPDPPSO", "Consentimiento en accesos", "Borrado con constancia"].map((b) => (
              <span key={b} className="px-3 py-1.5 rounded-full border border-slate-700 bg-slate-900/60 text-xs font-mono text-slate-300">
                {b}
              </span>
            ))}
          </div>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight text-white">
            Abre el centro de mando.
          </h2>
          <p className="mt-5 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Cuatro vistas que se prenden por botón. La identificación por forma de caminar corre
            sobre un análisis real ya procesado; las demás se muestran como demostración de cómo
            se verán.
          </p>
          <button
            data-testid="cta-demo-btn"
            onClick={() => navigate("/demo")}
            className="mt-9 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-3.5 rounded-md transition-colors shadow-lg shadow-blue-600/20"
          >
            Abrir el centro de mando <ArrowUpRight className="h-4 w-4" />
          </button>
          <p className="mt-4 text-xs text-slate-600 font-mono">
            Motor de identificación basado en OpenGait, uso demostrativo · el equipo de captura y
            de vuelo es del cliente
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-900 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-[1.4fr_1fr_1fr] gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <img src={ASSETS.logo} alt="Kootixli" className="h-8 w-8 object-contain" />
              <span className="font-display font-extrabold text-xl text-white">Kootixli</span>
            </div>
            <p className="mt-4 text-sm text-slate-500 max-w-sm leading-relaxed">
              Plataforma de monitoreo e identificación para eventos masivos: registro en los
              accesos, centro de mando durante el evento y borrado con constancia al cerrar.
            </p>
            <button
              onClick={() => { toast.success("Gracias por tu interés. Te contactaremos pronto."); }}
              data-testid="footer-contact-btn"
              className="mt-5 inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
            >
              Contactar al equipo <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div>
            <h5 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-4">Proyecto</h5>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="#recorrido" className="hover:text-white">Cómo funciona</a></li>
              <li><a href="#centro-de-mando" className="hover:text-white">Centro de mando</a></li>
              <li><a href="#privacidad" className="hover:text-white">Privacidad y datos</a></li>
              <li><a href="#fases" className="hover:text-white">Fases</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-4">Manejo de datos</h5>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>LFPDPPP</li>
              <li>LGPDPPSO</li>
              <li>Consentimiento en los accesos</li>
              <li>La decisión es de una persona</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-900 py-5">
          <p className="text-center text-xs text-slate-600 font-mono">
            © 2026 Kootixli · Zulunity
          </p>
        </div>
      </footer>
    </div>
  );
}
